/**
 * Review coverage by shift.
 *
 * A COVERAGE AND LOAD view, not a leaderboard: it answers "was this shift's work
 * covered, and by how many people", never "who reviewed the most". That is why
 * reviewers are listed unranked, why the shift totals lead, and why a reviewer's
 * counts sit beside their role rather than in a sorted column.
 *
 * Names are never invented — an unnamed principal falls back to `Reviewer #id`
 * and its role chip, both supplied by the backend.
 */
import { useTranslation } from "react-i18next";
import { Moon, Sunrise, Sun, Sunset } from "lucide-react";
import { cn } from "@/ui/lib/utils";
import { describeShift, shiftStartHour, type ShiftDaypart } from "@/alerts/utils/shift-descriptor";
import {
  reviewerInitials,
  type ReviewActivityView,
  type ShiftReviewSection,
} from "@/alerts/utils/reviewer-breakdown";

const DAYPART_ICON: Record<ShiftDaypart, typeof Moon> = {
  night: Moon,
  morning: Sunrise,
  day: Sun,
  evening: Sunset,
};

/** Shift state, always carried in words as well as color. */
const STATUS_COPY: Record<
  string,
  {
    labelKey:
      | "shiftCoverage.status.inProgress"
      | "shiftCoverage.status.complete"
      | "shiftCoverage.status.notStarted";
    className: string;
  }
> = {
  in_progress: {
    labelKey: "shiftCoverage.status.inProgress",
    className: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  },
  complete: {
    labelKey: "shiftCoverage.status.complete",
    className: "border-border text-muted-foreground",
  },
  not_started: {
    labelKey: "shiftCoverage.status.notStarted",
    className: "border-border text-muted-foreground",
  },
};

const ReviewerList = ({ view }: { view: ReviewActivityView | null }) => {
  const { t } = useTranslation("alerts");
  if (!view || view.isEmpty) {
    return <p className="text-[11px] text-muted-foreground">{t("shiftCoverage.noReviews")}</p>;
  }
  return (
    <div className="space-y-1.5">
      {view.rows.map((row) => (
        <div key={row.key} className="flex items-center gap-2 text-xs">
          <span
            aria-hidden
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-[9px] font-medium text-muted-foreground"
          >
            {reviewerInitials(row.label)}
          </span>
          <span className="truncate">{row.label}</span>
          <span className="shrink-0 rounded-full border border-border px-1.5 text-[10px] text-muted-foreground">
            {row.roleLabel}
          </span>
          <span className="ml-auto shrink-0 tabular-nums text-muted-foreground">
            {row.reviewsCompleted}
          </span>
        </div>
      ))}
      {view.otherReviewers && (
        <p className="text-[11px] text-muted-foreground">
          {t("shiftCoverage.moreReviewers", {
            n: view.otherReviewers.reviewerCount,
            reviews: view.otherReviewers.reviewsCompleted,
          })}
        </p>
      )}
      {view.unattributed && view.unattributed.reviewsCompleted > 0 && (
        <p className="text-[11px] text-muted-foreground">
          {t("shiftCoverage.unattributed", { n: view.unattributed.reviewsCompleted })}
        </p>
      )}
    </div>
  );
};

export const AlertShiftCoverage = ({ sections }: { sections: ShiftReviewSection[] }) => {
  const { t } = useTranslation("alerts");
  return (
  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
    {sections.map((section) => {
      const descriptor = describeShift(section.shiftKey, section.label);
      const Icon =
        shiftStartHour(section.shiftKey, section.label) == null
          ? Sun
          : DAYPART_ICON[descriptor.daypart];
      const status = STATUS_COPY[section.status] ?? STATUS_COPY.not_started;

      return (
        <div key={section.shiftKey} className="rounded-lg border border-border p-3">
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{descriptor.name}</p>
              {/* The exact window is kept so no information is lost to the name. */}
              <p className="truncate text-[11px] text-muted-foreground">{descriptor.range}</p>
            </div>
            <span
              className={cn(
                "ml-auto shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] font-medium",
                status.className,
              )}
            >
              {t(status.labelKey)}
            </span>
          </div>

          <div className="mt-3 grid grid-cols-3 gap-2 border-y border-border py-2 text-center">
            <div>
              <p className="text-sm font-semibold tabular-nums">{section.received ?? "—"}</p>
              <p className="text-[10px] text-muted-foreground">{t("labels.received")}</p>
            </div>
            <div>
              <p className="text-sm font-semibold tabular-nums">
                {section.reviewsCompleted ?? "—"}
              </p>
              <p className="text-[10px] text-muted-foreground">{t("labels.reviewed")}</p>
            </div>
            <div>
              <p className="text-sm font-semibold tabular-nums">{section.reviewersActive}</p>
              <p className="text-[10px] text-muted-foreground">{t("labels.reviewers")}</p>
            </div>
          </div>

          <div className="mt-2">
            <ReviewerList view={section.view} />
          </div>
        </div>
      );
    })}
  </div>
  );
};
